import React from 'react';
import styled from 'styled-components';
import { Radio, RadioGroup } from '@zocdoc-frontend-common/components-radio';
import {
    breakpoint,
    Breakpoints,
    getWindowBreakpoint,
} from '@zocdoc-frontend-common/styles-grid';
import {
    NEW_OR_EXISTING_PATIENT_STRINGS,
    PatientRadioValues,
    RadioGroupOrientations,
} from './constants';

const radioGroupProps = (
    orientation,
    isNewPatient,
    onRadioChange,
    hasError
) => {
    let selectedValue = null;
    // Null state for isNewPatient implies we are unable to make an assumption
    // on whether the patient is new or existing
    if (isNewPatient !== null) {
        selectedValue = isNewPatient
            ? PatientRadioValues.NEW_PATIENT
            : PatientRadioValues.EXISTING_PATIENT;
    }

    return {
        'data-test': `patient-radiogroup-${orientation}`,
        name: 'isNewPatient',
        onChangeCallback: onRadioChange,
        orientation,
        selectedValue,
        hasError,
        'aria-activedescendant': 'new-patient',
        'aria-labelledby': 'new-or-existing-patient-label',
    };
};

const renderRadioGroup = (
    radios,
    orientation,
    isNewPatient,
    onRadioChange,
    hasError
) => (
    <RadioGroup
        key={!!isNewPatient} //since this is an uncontrolled component we need to force rerender when 'isNewPatient' is changed externally
        {...radioGroupProps(orientation, isNewPatient, onRadioChange, hasError)}
    >
        {radios}
    </RadioGroup>
);

const NewOrExistingPatient = ({
    alwaysShowVertical = false,
    hasError,
    isNewPatient,
    onRadioChange,
}) => {
    const radios = [
        <StyledRadio
            id="new-patient"
            key="new patient"
            data-test="radio-new-patient"
            data-uem-id={`radio-new-patient${isNewPatient ? '-active' : ''}`}
            sideBySideForDesktop={!alwaysShowVertical}
            text={NEW_OR_EXISTING_PATIENT_STRINGS.NewPatient}
            value={PatientRadioValues.NEW_PATIENT}
        />,
        <StyledRadio
            id="existing-patient"
            key="existing patient"
            data-test="radio-seen-doctor"
            data-uem-id={`radio-seen-doctor${isNewPatient ? '' : '-active'}`}
            sideBySideForDesktop={!alwaysShowVertical}
            text={NEW_OR_EXISTING_PATIENT_STRINGS.ExistingPatient}
            value={PatientRadioValues.EXISTING_PATIENT}
        />,
    ];

    const windowBreakpoint = getWindowBreakpoint();
    const isSmallBreakPoint = [
        Breakpoints.small,
        Breakpoints.xsmall,
        Breakpoints.xxsmall,
    ].includes(windowBreakpoint);

    const orientation =
        alwaysShowVertical || isSmallBreakPoint
            ? RadioGroupOrientations.Vertical
            : RadioGroupOrientations.Horizontal;

    return renderRadioGroup(
        radios,
        orientation,
        isNewPatient,
        onRadioChange,
        hasError
    );
};

const StyledRadio = styled(Radio)`
    width: 100%;

    ${breakpoint.medium} {
        ${(p) => (p.sideBySideForDesktop ? 'flex: 1' : '')};

        *::-ms-backdrop,
        &:nth-child(2) {
            min-width: 13%;
        }
    }
`;

export default NewOrExistingPatient;
